import { LegalDocumentPage } from "@/components/legal/LegalDocumentPage";
import { getBrandConfig } from "@/config/brand";

function buildTermsMarkdown(brandName: string) {
  return `# ${brandName} - Cloud Terms of Service

**Effective Date:** March 25, 2026  
**Last Updated:** March 25, 2026  
**Applies To:** Customers using ${brandName} as a WYNI-hosted cloud service ("Cloud Service")

---

## Part A: Cloud Terms of Service

### 1. Scope

These Terms apply when Customer accesses and uses ${brandName} as a hosted cloud service.

### 2. Contract Structure (MSA-Aligned)

These Terms are governed by, and incorporated into, the applicable:

- Master Services Agreement (MSA)
- Order Form and/or Statement of Work (SOW)
- Service Level Agreement (SLA), where applicable
- Data Processing Addendum (DPA), where applicable
- Security Addendum, where applicable

If there is a conflict, precedence is:

1. Order Form / SOW / SLA
2. DPA / Security Addendum
3. MSA
4. These Terms

### 3. Access and Use Rights

WYNI grants Customer a limited, non-exclusive, non-transferable right to access and use the Cloud Service during the subscription term for internal business use by authorized users.

### 4. Customer Account Responsibilities

Customer is responsible for:

- Managing authorized users and role permissions
- Protecting account credentials and enforcing SSO/security policies
- Ensuring lawful use of service features and uploaded content
- Managing integration permissions and connector access

### 5. Customer Data and Service License

Customer retains ownership of Customer Data. Customer grants WYNI a limited right to host, process, transmit, analyze, and index Customer Data as needed to provide, secure, and support the Cloud Service.

### 6. Acceptable Use

Customer must not:

- Use the service in violation of law
- Upload malicious, unlawful, or infringing content
- Attempt unauthorized access or bypass security controls
- Abuse technical limits or integration APIs

### 7. Third-Party Services

The Cloud Service may rely on third-party providers and integration APIs. WYNI is not responsible for third-party outages, policy changes, API changes, or service discontinuation.

### 8. AI Output Disclaimer

AI outputs may be inaccurate or incomplete. Customer is responsible for verifying outputs before use in business, legal, financial, medical, HR, or regulatory contexts.

### 9. Availability and Changes

WYNI may update features and infrastructure during the subscription term. Availability commitments, maintenance windows, and service credits (if any) are governed by SLA/Order Form.

### 10. Fees and Billing

Fees, billing cycle, renewal, taxes, and overage terms are defined in the applicable Order Form/subscription agreement.

### 11. Suspension and Termination

WYNI may suspend service access for material breach, non-payment, security risk, abuse, or legal compliance reasons as permitted by contract.

### 12. Post-Termination Data Handling

Data export windows and deletion timelines are governed by the applicable Order Form, DPA, and retention terms.

### 13. Confidentiality

Each party must protect the other party's confidential information and use it only as required to perform the agreement.

### 14. Warranty Disclaimer

Except as expressly stated in MSA/SLA/Order Form, the Cloud Service is provided "as is" and "as available."

### 15. Limitation of Liability

Liability limits, exclusions, and caps are governed by the applicable MSA/Order Form and applicable law.

### 16. Governing Law

As defined in the applicable MSA/Order Form.

### 17. Contact

Legal/support contact: support@wyni.technology (Miss Winnie Leung)`;
}

const TermsOfUse = () => {
  const { brandName } = getBrandConfig();
  return (
    <LegalDocumentPage
      title="Terms of Use"
      description={`The cloud-service terms governing access, acceptable use, customer responsibilities, and contract alignment for ${brandName}.`}
      content={buildTermsMarkdown(brandName)}
    />
  );
};

export default TermsOfUse;
